import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Shield } from "lucide-react";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import ConversionCtas from "../../components/content/ConversionCtas";

export default function WhatIsSeaMossPage() {
  const path = "/learn/what-is-sea-moss";
  return (
    <SiteShell theme="dark">
      <SEO
        title="What Is Sea Moss? Simple MossMatrix Guide"
        description="Sea moss is a type of red seaweed, commonly prepared into gel and stirred into shakes, drinks, oats, teas, and recipes at home. MossMatrix explains the process in plain English, without miracle claims."
        path={path}
      />
      <ContentPageLayout
        eyebrow="Learn"
        title="Sea moss is simple once you understand it."
        subtitle="It starts as dried seaweed, gets soaked and cleaned, then blended into a smooth gel with spring water. From there it is a mineral-rich stir-in, popular in daily wellness routines, not a replacement for real meals or medical care."
      >
        <div className="rounded-2xl border border-cream bg-white p-6 md:p-8 mb-10 max-w-3xl">
          <p className="text-sm text-botanical/85 leading-relaxed">
            Sea moss is a type of <strong className="text-forest">red seaweed</strong>, commonly prepared into a gel and stirred into shakes, drinks, oats, teas, and recipes at home. MossMatrix keeps the ingredient list short: sea moss, spring water, and optional powdered herbs you choose in the builder.
          </p>
          <p className="mt-4 text-sm text-botanical/85 leading-relaxed">
            We like the tone candid: it is mineral-rich, may support variety in what you eat, and has been traditionally used in coastal kitchens for generations. None of that means it cures anything.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <section className="rounded-2xl border border-cream bg-offwhite p-6">
            <h2 className="font-serif text-xl text-forest flex items-center gap-2">
              <Leaf className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
              <span>Why it shows up on wellness feeds</span>
            </h2>
            <p className="mt-3 text-sm text-botanical/85 leading-relaxed">
              It’s low-effort to blend into drinks, carries minerals people associate with “feeling looked after,” and fits a modern apothecary mood without cluttering your counter with ten bottles.
            </p>
          </section>
          <section className="rounded-2xl border border-cream bg-offwhite p-6">
            <h2 className="font-serif text-xl text-forest flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
              <span>How MossMatrix handles claims</span>
            </h2>
            <p className="mt-3 text-sm text-botanical/85 leading-relaxed">
              We avoid language about curing, treating, detoxing, or guaranteed weight loss. If you need health guidance, a qualified practitioner beats a product page every time.
            </p>
          </section>
        </div>

        <ConversionCtas />

        <p className="text-sm text-botanical/80 text-center mt-10">
          <Link to="/learn/gold-vs-purple-sea-moss" className="underline hover:text-forest">
            Gold vs purple
          </Link>
          {" · "}
          <Link to="/learn/sea-moss-nutrition" className="underline hover:text-forest">
            Minerals & amino acids
          </Link>
          {" · "}
          <Link to="/learn/sea-moss-research" className="underline hover:text-forest">
            Research
          </Link>
          {" · "}
          <Link to="/learn" className="underline hover:text-forest">
            All guides
          </Link>
          {" · "}
          <Link to="/shop" className="underline hover:text-forest">
            Shop
          </Link>
          {" · "}
          <Link to="/shop" className="underline hover:text-forest">Choose your sea moss</Link>{" � "}<Link to="/mix-ins" className="underline hover:text-forest">Explore mix-in ideas</Link>
          {" · "}
          <Link to="/ingredients" className="underline hover:text-forest">
            Ingredients
          </Link>
          {" · "}
          <Link to="/faq" className="underline hover:text-forest">
            FAQ
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
